import React, { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import { useTailwindBrand } from '../../../Components/shared';
import { getActiveAppBrandConfig } from '../../../config/environment.config';
import { useAiciOnboardingContexte } from '../../../Contexts/AiciOnboardingContexte';

interface ActivationStep {
    title: string;
    detail: string;
    link?: string;
    linkLabel?: string;
}

const ConfirmationStep = () => {
    const { classes, brandConfig } = useTailwindBrand();
    const { teachrFirstName } = useAiciOnboardingContexte();
    const supportMail = process.env.REACT_APP_MAIL_SUPPORT;
    const appName = getActiveAppBrandConfig();
    const primary = brandConfig.colors.primary;

    // L'étape de confirmation couvre deux situations : un compte URSSAF
    // fraîchement créé (parcours par défaut) ou un compte déjà existant.
    const [mode, setMode] = useState<'new' | 'existing'>('new');

    const NEW_ACCOUNT_STEPS: ActivationStep[] = [
        {
            title: "Ouvrez l'e-mail de l'URSSAF",
            detail: "Vous venez de le recevoir normalement, mais cela peut prendre jusqu'à 5 minutes (pensez à vérifier vos spams). Vous n'avez rien reçu ? ",
            link: 'https://www.particulier.urssaf.fr/#/lien-activation-compte',
            linkLabel: 'Renvoyer le lien',
        },
        {
            title: 'Configurez votre mot de passe',
            detail: 'Cliquez sur « Configurer mon mot de passe » dans l\'e-mail.',
        },
        {
            title: 'Connectez-vous',
            detail: 'Connectez-vous avec votre e-mail et le mot de passe que vous venez de créer.',
        },
        {
            title: 'Activez votre compte',
            detail: 'Acceptez les CGU et les conditions SEPA puis cliquez sur « Activer mon compte ».',
        },
    ];

    const EXISTING_ACCOUNT_STEPS: ActivationStep[] = [
        {
            title: 'Connectez-vous à votre compte URSSAF',
            detail: 'Rendez-vous sur particulier.urssaf.fr et connectez-vous avec votre e-mail et votre mot de passe habituels. ',
            link: 'https://www.particulier.urssaf.fr/',
            linkLabel: 'Me connecter',
        },
        {
            title: `Repérez ${appName} dans vos intervenants`,
            detail: `Dans la liste de vos intervenants, ${appName} apparaît automatiquement aucune recherche nécessaire.`,
        },
        {
            title: `Autorisez ${appName}`,
            detail: `Autorisez ${appName} comme nouvel intervenant, et c'est tout !${teachrFirstName ? ` Votre professeur ${teachrFirstName} pourra alors déclarer vos cours.` : ''}`,
        },
    ];

    const TABS: { id: 'new' | 'existing'; label: string; Icon: typeof MarkEmailReadOutlinedIcon }[] = [
        { id: 'new', label: "Nouveau compte URSSAF", Icon: MarkEmailReadOutlinedIcon },
        { id: 'existing', label: "J'ai déjà un compte", Icon: LoginOutlinedIcon },
    ];

    const steps = mode === 'new' ? NEW_ACCOUNT_STEPS : EXISTING_ACCOUNT_STEPS;
    const followUp = teachrFirstName ? ` Votre professeur ${teachrFirstName} pourra ensuite déclarer vos cours.` : '';

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <CheckCircleIcon sx={{ color: primary, fontSize: 34 }} />
                <h2 className={`text-xl lg:text-2xl font-extrabold ${classes.primaryText} ${classes.fontFamily}`}>
                    {mode === 'new' ? 'Votre compte URSSAF a bien été créé' : 'Dernière étape côté URSSAF'}
                </h2>
            </div>

            <div className="flex items-start gap-3 p-4 border border-amber-200 bg-amber-50 rounded-xl">
                <WarningAmberIcon sx={{ color: '#d97706' }} />
                <div>
                    <p className={`font-extrabold text-amber-900 ${classes.fontFamily}`}>
                        {mode === 'new' ? "Compte en attente d'activation" : 'Une autorisation à donner'}
                    </p>
                    <p className={`text-sm text-amber-800 ${classes.fontFamily}`}>
                        {mode === 'new'
                            ? `Pour utiliser l'avance immédiate de crédit d'impôt, vous devez terminer la configuration de votre compte URSSAF en suivant les étapes ci-dessous.${followUp}`
                            : `Votre compte URSSAF existe déjà : il vous suffit d'autoriser ${appName} comme intervenant pour activer l'avance immédiate de crédit d'impôt.${followUp}`}
                    </p>
                </div>
            </div>

            <div className="flex flex-col gap-5">
                <div>
                    <h3 className={`text-base font-bold ${classes.primaryText} ${classes.fontFamily}`}>Comment activer votre compte ?</h3>
                    <p className={`mt-1 text-sm text-gray-500 ${classes.fontFamily}`}>
                        Choisissez votre situation pour voir les étapes adaptées.
                    </p>
                </div>

                {/* Sélecteur de scénario */}
                <div className="grid grid-cols-2 gap-1.5 p-1.5 bg-gray-100 rounded-2xl">
                    {TABS.map(({ id, label, Icon }) => {
                        const active = mode === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setMode(id)}
                                aria-pressed={active}
                                className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 ${classes.fontFamily} ${active ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                style={active ? { backgroundColor: primary } : undefined}
                            >
                                <Icon sx={{ fontSize: 18 }} />
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Timeline des étapes */}
                <ol className={`flex flex-col ${classes.fontFamily}`}>
                    {steps.map((step, i) => {
                        const isLast = i === steps.length - 1;
                        return (
                            <li key={`${mode}-${i}`} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                    <span
                                        className="flex items-center justify-center w-10 h-10 text-sm font-bold text-white rounded-full shrink-0 shadow-md ring-4 ring-white"
                                        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)` }}
                                    >
                                        {i + 1}
                                    </span>
                                    {!isLast && (
                                        <span
                                            className="w-0.5 flex-1 my-1 rounded-full"
                                            style={{ backgroundColor: primary, opacity: 0.2 }}
                                            aria-hidden
                                        />
                                    )}
                                </div>
                                <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-6'}`}>
                                    <p className={`font-bold ${classes.text}`}>{step.title}</p>
                                    <p className="mt-1 text-sm leading-relaxed text-gray-500">
                                        {step.detail}
                                        {step.link && (
                                            <a
                                                href={step.link}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={`${classes.primaryText} font-bold underline`}
                                            >
                                                {step.linkLabel ?? 'Appuyer ici'}
                                            </a>
                                        )}
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>

            <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className={`text-sm text-gray-600 ${classes.fontFamily}`}>
                    {mode === 'new'
                        ? <p>L'e-mail de validation provient de l'adresse « ne-pas-repondre@urssaf.fr ». Vous disposez de 7 jours pour valider votre compte après réception. Passé ce délai, vous pouvez <a
                            href={"https://www.particulier.urssaf.fr/#/lien-activation-compte"}
                            target="_blank"
                            rel="noreferrer"
                            className={`${classes.primaryText} font-bold underline`}
                        >
                            cliquer ici
                        </a> pour générer un nouveau lien d'activation.</p> 
                        : `Une fois ${appName} autorisé comme intervenant, l'avance immédiate s'applique automatiquement à vos prochains cours. `}
                    Pour toute question, {appName} reste à votre
                    disposition{supportMail ? ` à l'adresse ${supportMail}` : ''}.
                </p>
            </div>

            {/* Accès à l'espace client : le compte a été créé sans mot de passe
                connu, la première connexion passe par un lien de choix du mot de passe */}
            <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className={`font-bold ${classes.text} ${classes.fontFamily}`}>Votre espace {appName}</p>
                <p className={`mt-1 text-sm text-gray-600 ${classes.fontFamily}`}>
                    Vous y retrouverez vos cours, vos devis et vos factures. Pour y accéder la première fois,
                    rendez-vous sur la page de connexion, cliquez sur <strong className={classes.primaryText}>« Recevoir mon lien de première connexion »</strong>
                    et indiquez l'adresse e-mail que vous venez d'utiliser : vous recevrez un lien pour choisir votre mot de passe.
                </p>
            </div>
        </div>
    );
};

export default ConfirmationStep;
